import { Component, EventEmitter, Input, OnInit, Pipe, PipeTransform, Type } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';
import { ShareValuesService } from 'src/app/services/sharedValues/share-values.service';
import { NgForm } from '@angular/forms';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { EllipsisPipe } from 'src/app/pipes/EllipsisPipe';
import { appLanguageOptions, PickType } from 'src/app/utilities/constant';
import { getMeasureTypeTranslationKey } from 'src/app/utilities/utils';
import { ControlMeasureSuggestionsService, SuggestedControlMeasure, SuggestionContext } from 'src/app/services/control-measure-suggestions/control-measure-suggestions.service';
import { ControlMeasureGenerationService, GeneratedControlMeasure } from 'src/app/services/control-measure-generation/control-measure-generation.service';
import { UserAuthService } from 'src/app/services/user-auth/user-auth.service';

@Component({
  selector: 'app-pick-hazard',
  templateUrl: './pick-hazard.component.html',
  styleUrls: ['./pick-hazard.component.scss'],
})
export class PickHazardComponent implements OnInit {
  private static readonly DEFAULT_LANGUAGE_CODE = 'en';
  public readonly getMeasureTypeTranslationKey = getMeasureTypeTranslationKey;
  PickType = PickType;
  @Input() type: string;
  @Input() measureType: any;
  /** Hazard being assessed. Absent when the picker is opened without one, which
   *  leaves the Suggested section hidden rather than showing an unranked list. */
  @Input() suggestionContext: SuggestionContext | null = null;
  /** Hazard fields the generation route uses that the ranking route has no use for.
   *  Absent when the picker is opened from anywhere that does not hold them. */
  @Input() generationExtras: {
    hazardName?: string; hazardType?: string; hazardLocation?: string;
    hazardSources?: string; hazardConsequences?: string;
  } | null = null;

  suggestions: SuggestedControlMeasure[] = [];
  suggestionTotal = 0;
  suggestionGroups: { typeId: number; typeName: string; items: SuggestedControlMeasure[] }[] = [];
  suggestionsCollapsed = false;
  /** Set when the selected row came from a suggestion, so save() reports the type the
   *  measure actually is rather than whatever the dropdown happens to show. */
  private selectedSuggestionType: any = null;

  /** ISO 12100 three-step method, matching the order GetMeasureType returns. */
  private static readonly TYPE_ORDER = [4, 1, 3, 2];
  private static readonly COLLAPSE_KEY = 'pickHazard.suggestionsCollapsed';
  suggestionsLoading = false;
  /** null while loading or on success; a translation key explaining an empty section. */
  suggestionsNotice: string | null = null;

  generated: GeneratedControlMeasure[] = [];
  generatedLoading = false;
  generatedCollapsed = false;
  /** null while loading or on success; a translation key explaining an empty section. */
  generatedNotice: string | null = null;
  private static readonly GENERATED_COLLAPSE_KEY = 'pickHazard.generatedCollapsed';
  initialHazardsList = [];
  libraryLoading = true;
  libraryLoadFailed = false;
  filterTerm: string;
  searchTerm='';
  private onScrolled = new EventEmitter();
  url: string;
  count = 30;
  increasedCount = 0;
  limitReached = false;
  focusedCardIndex = -1;

  selectedData;
  selectedMeasureType: any;

  /** Dropdown items rendered as 'English (en)' etc. with the app-internal code as the bound value. */
  public readonly languageOptions = appLanguageOptions.map(lang => ({
      code: lang.code,
      display: `${lang.label} (${lang.code})`
    }));
  /** Currently selected language code for the From-Library filter. Defaults to the active app language. */
  public selectedLanguageCode: string | null = null;

  public get aiMeasuresEnabled(): boolean {
    return this.userAuthService.hasPermission('AI_MEASURES');
  }

  constructor(public backendService: BackendCallService, public loadingController: LoadingController,
    public sharedValue: ShareValuesService, public modalController: ModalController, public ellipsis: EllipsisPipe,
    public translate: TranslateService, private suggestionsService: ControlMeasureSuggestionsService,
    private generationService: ControlMeasureGenerationService, private userAuthService: UserAuthService) { }

  ionViewWillEnter() {
    // if (this.type == 'Pick Initial Hazard') this.getInitialHazardsList();
    // else this.getCounterMeasuresList()
  }

  showSearchBarIcon(){

  }

  ngOnInit() {
    this.selectedLanguageCode = this.resolveDefaultLanguageCode();
    try {
      this.suggestionsCollapsed = localStorage.getItem(PickHazardComponent.COLLAPSE_KEY) === 'true';
      this.generatedCollapsed = localStorage.getItem(PickHazardComponent.GENERATED_COLLAPSE_KEY) === 'true';
    } catch { /* private browsing */ }
    this.loadSuggestions();
    this.loadGenerated();
    this.subscribeScroll();
    this.onScrolled.emit();
    this.translate.get(this.sharedValue.measureTypes.map(t => getMeasureTypeTranslationKey(t.measure_type_name))).subscribe(translations => {
      this.sharedValue.measureTypes = this.sharedValue.measureTypes.map(t => ({
        ...t,
        label: translations[getMeasureTypeTranslationKey(t.measure_type_name)]
      }));
    });
  }

  /** Fills the Suggested section. A failure leaves it empty and the library list,
   *  which is the whole picker without this feature, is unaffected. */
  async loadSuggestions() {
    // Suggestions are only available to users with the AI_MEASURES permission.
    if (!this.aiMeasuresEnabled) { return; }
    if (this.type !== PickType.PICK_MEASURE) { return; }
    // The hazard description is the strongest signal but often unwritten when a
    // consultant reaches the measures step, so the roadmap step and machine are
    // ranked against instead of showing nothing.
    const ctx = this.suggestionContext;
    if (!ctx || !(ctx.hazard?.trim() || ctx.stepName?.trim() || ctx.machineName?.trim())) {
      this.suggestionsNotice = "add_hazard_description_for_suggestions";
      return;
    }
    this.suggestionsLoading = true;
    try {
      const result = await this.suggestionsService.forHazard({
        ...this.suggestionContext,
        language: this.selectedLanguageCode || '',
      });
      this.suggestions = result.suggestions;
      this.suggestionTotal = result.total;
      this.buildSuggestionGroups();
      if (!this.suggestions.length) { this.suggestionsNotice = "no_close_library_matches"; }
    } catch (e) {
      console.error("[suggested] request failed", e);
      this.suggestionsNotice = "suggestions_unavailable";
    } finally {
      this.suggestionsLoading = false;
    }
  }

  onSuggestionSelect(suggestion: SuggestedControlMeasure) {
    this.selectedData = suggestion;
    this.selectedSuggestionType = this.measureTypeFor(suggestion.measureTypeId);
    this.focusedCardIndex = -1;
  }

  /** Fills the AI-recommended section. A failure leaves it empty; the ranked library
   *  rows and the full list below it are unaffected. */
  async loadGenerated() {
    if (!this.aiMeasuresEnabled) { return; }
    if (this.type !== PickType.PICK_MEASURE) { return; }
    // Generation answers the assessor's written observation. The machine name and
    // checklist question on their own describe a subsystem, not a finding.
    const hazard = this.suggestionContext?.hazard?.trim();
    if (!hazard) {
      this.generatedNotice = "add_hazard_description_for_suggestions";
      return;
    }
    this.generatedLoading = true;
    try {
      this.generated = await this.generationService.forHazard({
        hazard,
        hazardName: this.generationExtras?.hazardName,
        hazardType: this.generationExtras?.hazardType,
        hazardLocation: this.generationExtras?.hazardLocation,
        hazardSources: this.generationExtras?.hazardSources,
        hazardConsequences: this.generationExtras?.hazardConsequences,
        machineName: this.suggestionContext?.machineName,
        // The step alone, without its section path: the prompt was measured on that shape.
        stepName: this.suggestionContext?.stepName?.split(' > ').pop(),
      });
      if (!this.generated.length) { this.generatedNotice = "no_measure_for_hazard"; }
    } catch (e) {
      console.error("[ai-recommended] request failed", e);
      this.generatedNotice = "ai_recommended_measures_unavailable";
    } finally {
      this.generatedLoading = false;
    }
  }

  /** Leaves selectedSuggestionType null so save() reports whatever the measure-type
   *  dropdown shows. Which of the four ISO 12100 types a measure belongs to is the
   *  consultant's call, not the model's. */
  onGeneratedSelect(measure: GeneratedControlMeasure) {
    this.selectedData = measure;
    this.selectedSuggestionType = null;
    this.focusedCardIndex = -1;
  }

  toggleGenerated() {
    this.generatedCollapsed = !this.generatedCollapsed;
    try {
      localStorage.setItem(PickHazardComponent.GENERATED_COLLAPSE_KEY, String(this.generatedCollapsed));
    } catch { /* private browsing */ }
  }

  get showGenerated(): boolean {
    if (!this.aiMeasuresEnabled) { return false; }
    if (this.type !== PickType.PICK_MEASURE) { return false; }
    if (this.searchTerm?.trim()) { return false; }
    return this.generatedLoading || this.generated.length > 0 || !!this.generatedNotice;
  }

  /** Groups the ranked list under type headings, in hierarchy order. A type with no
   *  relevant measure gets no heading rather than an empty one. */
  private buildSuggestionGroups() {
    this.suggestionGroups = PickHazardComponent.TYPE_ORDER
      .map(typeId => ({
        typeId,
        typeName: this.suggestions.find(s => s.measureTypeId === typeId)?.measureType || '',
        items: this.suggestions.filter(s => s.measureTypeId === typeId),
      }))
      .filter(group => group.items.length > 0);
  }

  private measureTypeFor(measureTypeId: number) {
    return (this.sharedValue?.measureTypes || []).find(t => t.measure_type_id === measureTypeId) || null;
  }

  toggleSuggestions() {
    this.suggestionsCollapsed = !this.suggestionsCollapsed;
    try {
      localStorage.setItem(PickHazardComponent.COLLAPSE_KEY, String(this.suggestionsCollapsed));
    } catch { /* private browsing */ }
  }

  /** Hidden while searching, because someone typing a query already knows what they
   *  want, and when there is nothing to show and nothing useful to say about why. */
  get showSuggestions(): boolean {
    if (!this.aiMeasuresEnabled) { return false; }
    if (this.type !== PickType.PICK_MEASURE) { return false; }
    if (this.searchTerm?.trim()) { return false; }
    return this.suggestionsLoading || this.suggestions.length > 0 || !!this.suggestionsNotice;
  }

  get showEmptyState(): boolean {
    return !this.libraryLoading && !this.libraryLoadFailed && !this.initialHazardsList.length
      && !this.showGenerated && !this.showSuggestions;
  }

  /** Resolves the default language code for the From-Library filter. */
  private resolveDefaultLanguageCode(): string {
    const activeLanguage = (this.translate.currentLang || this.translate.defaultLang || PickHazardComponent.DEFAULT_LANGUAGE_CODE).toLowerCase();
    const normalised = activeLanguage === 'es' ? 'sp' : activeLanguage;
    return this.languageOptions.some(option => option.code === normalised)
      ? normalised
      : PickHazardComponent.DEFAULT_LANGUAGE_CODE;
  }

  /**
   * Handler for the language dropdown. Resets the list and re-fetches so the backend
   * payload carries the new languageCode.
   */
  onLanguageChange(code: string | null) {
    this.selectedLanguageCode = code || null;
    this.suggestions = [];
    this.suggestionGroups = [];
    this.suggestionTotal = 0;
    this.suggestionsNotice = null;
    this.loadSuggestions();
    this.limitReached = false;
    this.increasedCount = 0;
    this.initialHazardsList = [];
    this.selectedData = null;
    this.focusedCardIndex = -1;
    this.onScrolled.next(true);
  }

  onMeasureTypeChange(selectedType) {
    this.measureType = selectedType;
    this.limitReached = false;
    this.increasedCount = 0;
    this.initialHazardsList = [];
    this.selectedData = null;
    this.focusedCardIndex = -1;
    this.onScrolled.next(true);
  }


  subscribeScroll() {
    this.onScrolled.pipe(tap(() => {
      this.libraryLoading = true;
      this.libraryLoadFailed = false;
    }), debounceTime(100), switchMap((res) => {
      const payload =
      {
        count: this.count,
        increasedCount: this.increasedCount,
        search: this.searchTerm,
        languageCode: this.selectedLanguageCode || ''
      };
      if (this.type == PickType.PICK_MEASURE && this.measureType != null) {
        payload['controlMeasureTypeId'] = this.measureType.measure_type_id ? this.measureType.measure_type_id : 3;
      }
      if (this.type == PickType.PICK_INITIAL_HAZARD) {return this.backendService.getInitialHazardsListWithIndex(payload);}
      else {return this.backendService.getCounterMeasuresListWithIndex(payload);}
    })).subscribe((data: any) => {
        this.libraryLoading = false;
        if (this.searchTerm !== '') {this.increasedCount = this.increasedCount + this.count;}
        if (this.initialHazardsList.length && this.initialHazardsList[0].value.startsWith(this.searchTerm)) {
          if (data.length !== 0) {
            this.initialHazardsList = this.initialHazardsList.concat(...data);
          }else{
            this.increasedCount=0;
            this.limitReached = data.length !== this.count;
          }
        } else {
          this.increasedCount=0;
          if (data.length !== 0) {
            this.initialHazardsList = this.initialHazardsList.concat(...data);
            this.initialHazardsList = [];
          }else{
            this.increasedCount=0;
            this.limitReached = data.length !== this.count;
          }
        }
        this.increasedCount = this.increasedCount + this.count;
        this.initialHazardsList = this.initialHazardsList.concat(...data);
        setTimeout(() => {
        }, 500);
      },err=>{
        this.libraryLoading = false;
        this.libraryLoadFailed = true;
        console.error('[pick-hazard] library request failed', err);
      }
    );

  }


  getItems(ev: any) {
    const val = ev.target.value;
    if (this.onScrolled) {
      this.onScrolled.next(true);
      this.searchTerm = val;
      this.limitReached = false;
      this.increasedCount=0;
      this.initialHazardsList =[];
      // this.subscribeScroll();
      // this.onScrolled.emit();
    }

  }

  onScroll() {
    if (!this.limitReached) {
      this.onScrolled.emit(Date.now());
    }
  }

  close() {
    this.modalController.dismiss();
  }

 async getCounterMeasuresList() {
    this.backendService.getCounterMeasuresList()
      .subscribe(
        (data: any) => {
          this.initialHazardsList = data;
        },
        (err) => {
        }
      );
  }

  async presentLoading(msg) {
    const loading = await this.loadingController.create({
      cssClass: 'my-custom-loading',
      message: msg,
      spinner: 'bubbles'
    });
    await loading.present();
    return loading;
  }

  save() {
    const dataToSend: any = {
      selectedData: this.selectedData
    };
    if (this.type === PickType.PICK_MEASURE) {
      dataToSend.measureType = this.selectedSuggestionType || this.measureType;
    }

    this.modalController.dismiss(dataToSend);
  }

  getCardTabIndex(index: number): number {
    if (!this.initialHazardsList?.length) {
      return -1;
    }

    if (this.focusedCardIndex >= 0 && this.focusedCardIndex < this.initialHazardsList.length) {
      return this.focusedCardIndex === index ? 0 : -1;
    }

    const selectedIndex = this.initialHazardsList.findIndex((item) => item === this.selectedData);
    if (selectedIndex >= 0) {
      return selectedIndex === index ? 0 : -1;
    }

    return index === 0 ? 0 : -1;
  }

  onCardFocus(index: number) {
    this.focusedCardIndex = index;

    if (!this.selectedData) {
      this.onCardSelect(index);
    }
  }

  onCardSelect(index: number) {
    const selectedItem = this.initialHazardsList[index];
    if (!selectedItem) {
      return;
    }

    this.selectedSuggestionType = null;
    this.selectedData = selectedItem;
    this.focusedCardIndex = index;
  }

  onCardKeydown(event: KeyboardEvent, index: number) {
    if (!this.initialHazardsList?.length) {
      return;
    }

    if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter') {
      event.preventDefault();
      this.onCardSelect(index);
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusCardByIndex(index + 1);
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusCardByIndex(index - 1);
    }
  }

  private focusCardByIndex(index: number) {
    const total = this.initialHazardsList.length;
    if (!total) {
      return;
    }

    const nextIndex = (index + total) % total;
    this.onCardSelect(nextIndex);

    setTimeout(() => {
      const cardElements = Array.from(document.querySelectorAll('.radio-card')) as HTMLElement[];
      cardElements[nextIndex]?.focus();
    }, 0);
  }

  async getInitialHazardsList() {
   // const loader =await this.presentLoading('Please wait...');
    this.backendService.getInitialHazardsList()
      .subscribe(
        (data: any) => {
          this.initialHazardsList = data;
       //   loader && loader.dismiss();
        },
        (err) => {
       //   loader && loader.dismiss();
        }
      );
  }

}
@Pipe({
  name: 'searchFilter'
})

export class SearchFilterPipe implements PipeTransform {

  transform(value: any, args?: any): any {
    if (!value) {return null;}
    if (!args) {return value;}

    args = args.toLowerCase();

    return value.filter(function(data) {
      return JSON.stringify(data).toLowerCase().includes(args);
    });
  }

}
